import { Popup } from "@vis.gl/react-maplibre";
import { useRef, type FC, type ReactNode } from "react";

import { MAPLIBRE_STATION_PIN_POPUP_OFFSET_PX } from "./maplibre-station-pin.constants";
import { useMapLibrePopupAutoPan } from "./use-maplibre-popup-auto-pan";

interface IMapLibreAutoPanPopupProps {
  isOpen: boolean;
  latitude: number;
  longitude: number;
  /**
   * How far above the anchor the popup sits. Defaults to clearing a station pin; at MapLibre's own
   * default of 0 the tail lands on the marker and hides it.
   */
  offset?: number;
  /** Whether the popup draws MapLibre's own dismiss button. On by default; hover popups turn it off. */
  hasCloseButton?: boolean;
  /** A CSS width; unset keeps MapLibre's 240px cap, padding included, and `"none"` lets the content size it. */
  maxWidth?: string;
  /** Classes on the popup element, for host styles such as its stacking order. */
  className?: string;
  /** Changes when the content changes size, so the popup is measured and panned into view again. */
  contentKey?: string;
  /**
   * Whether a click on the map closes it. Safe only where the marker click stops propagating — otherwise
   * the click that opens the popup reaches the map and closes it again in the same gesture. Hover popups
   * leave it off and close on mouse-out instead.
   */
  shouldCloseOnMapClick?: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}

/**
 * A MapLibre popup that pans the map when it would otherwise open off-screen.
 *
 * Every popup on every map goes through here, for two reasons that are easy to get wrong one map at a
 * time. `useMapLibrePopupAutoPan` reaches the map through `@vis.gl/react-maplibre`'s context, so it does
 * all unless it is called from inside the map subtree — a popup assembled in the component that *renders*
 * the map silently loses its auto-pan. And the hook must keep running while the popup is shut, so the
 * open transition is what it observes; hence the conditional is inside, after the hook, rather than at
 * each call site.
 */
export const MapLibreAutoPanPopup: FC<IMapLibreAutoPanPopupProps> = ({
  isOpen,
  latitude,
  longitude,
  offset = MAPLIBRE_STATION_PIN_POPUP_OFFSET_PX,
  hasCloseButton = true,
  maxWidth,
  className,
  contentKey,
  shouldCloseOnMapClick = true,
  onClose,
  onMouseEnter,
  onMouseLeave,
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useMapLibrePopupAutoPan({ isOpen, contentRef, contentKey });

  if (!isOpen) {
    return null;
  }

  return (
    <Popup
      latitude={latitude}
      longitude={longitude}
      anchor="bottom"
      offset={offset}
      closeButton={hasCloseButton}
      // Only when set: MapLibre copies an explicit undefined over its own 240px default.
      {...(maxWidth ? { maxWidth } : {})}
      className={className}
      closeOnClick={shouldCloseOnMapClick}
      onClose={onClose}
    >
      <div
        ref={contentRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </div>
    </Popup>
  );
};
